import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Interval, PlanUnit } from 'components/types';

import { getMonthInterval, formatISOString } from 'utils/date';
import { getLocalState, updateLocalState } from 'utils/localStorage';

import { getLatest, PublicationInfo } from 'RestAPIs/cost-center/publication';

import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import DateInterval from 'components/shared/DateInterval';
import { getIntervalTitle } from 'components/shared/DateInterval.utils';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';

const HeaderStyles = styled.div`
  .MuiAutocomplete-root {
    min-width: 260px;
  }

  .side-container {
    width: 50%;
  }

  .only-planned {
    padding: 0;
    margin: 0 20px 0 10px;
  }

  .publication-info {
    margin-right: 10px;
    white-space: nowrap;
  }
`;

type Props = {
  onPlanUnitChanged: (value: PlanUnit[]) => void;
  onOnlyPlannedChanged: (state: boolean) => void;
  onIntervalChanged: (interval: Interval) => void;
  handlePrintAction: (printInfo: {
    planUnits: string;
    interval: string;
    lastPublishedDate: string;
    lastVisibleDate: string;
  }) => void;
};

export default function Header({
  onPlanUnitChanged,
  onOnlyPlannedChanged,
  onIntervalChanged,
  handlePrintAction
}: Props) {
  const { t } = useTranslation('common');
  const [selectedPlanUnits, setSelectedPlanUnits] = useState<PlanUnit[]>([]);
  const [publicationInfo, setPublicationInfo] =
    useState<PublicationInfo | null>(null);
  const [interval, setInterval] = useState(getMonthInterval());
  const [showOnlyPlanned, setShowOnlyPlanned] = useState(
    getLocalState().employeePortal?.teamRoster?.employees?.showOnlyPlanned ||
      false
  );

  const loadPublicationInfo = (value: PlanUnit[]) => {
    getLatest(value.map(({ id }) => id)).then(({ data }) => {
      setPublicationInfo(data);
    });
  };

  const onPlanUnitSelected = (value: PlanUnit[]) => {
    setSelectedPlanUnits(value);
    onPlanUnitChanged(value);
    updateLocalState({
      employeePortal: {
        teamRoster: {
          lastSelectedPlanUnitsIds: value.map(({ uniqId }) => uniqId)
        }
      }
    });
    setPublicationInfo(null);
    if (value.length === 1) {
      loadPublicationInfo(value);
    }
  };

  const toggleOnlyPlanned = () => {
    const newValue = !showOnlyPlanned;
    setShowOnlyPlanned(newValue);
    onOnlyPlannedChanged(newValue);
    updateLocalState({
      employeePortal: {
        teamRoster: {
          employees: {
            showOnlyPlanned: newValue
          }
        }
      }
    });
  };

  useEffect(() => {
    onIntervalChanged(interval);
    onOnlyPlannedChanged(showOnlyPlanned);
  }, []);

  const defaultPlanUnitSelected =
    getLocalState().employeePortal?.teamRoster?.lastSelectedPlanUnitsIds?.map(
      (uniqId) => ({ uniqId }) as PlanUnit
    ) || undefined;

  const lastPublishedDate = publicationInfo
    ? publicationInfo.lastPublishDate
      ? `<strong>${t('publishedUntil')}</strong>: ${formatISOString(
          publicationInfo.lastPublishDate
        )}`
      : t('rosterNotPublished')
    : '';

  const lastVisibleDate = publicationInfo
    ? publicationInfo.visibleUntilDate
      ? `<strong>${t('visibleUntil')}</strong>: ${formatISOString(
          publicationInfo.visibleUntilDate
        )}`
      : t('visibilityNotSet')
    : '';

  return (
    <HeaderStyles>
      <div className="d-flex v-center between">
        <div className="side-container d-flex v-bottom">
          <PlanUnitSelector
            hideGroups
            onSelected={onPlanUnitSelected}
            interval={interval}
            doNotPersist
            useEmployeePlanunits
            defaultValue={defaultPlanUnitSelected}
          />
          <FormControlLabel
            control={<Switch checked={showOnlyPlanned} />}
            label={`${t('plannedEmployees')}`}
            onChange={toggleOnlyPlanned}
            className="only-planned"
          />
        </div>
        <DateInterval
          onSelected={(i) => {
            setInterval(i);
            onIntervalChanged(i);
          }}
        />
        <div className="side-container d-flex end v-center">
          {selectedPlanUnits.length > 0 && (
            <>
              {publicationInfo && (
                <div className="text-right">
                  <span
                    className="publication-info inline-block"
                    dangerouslySetInnerHTML={{ __html: lastPublishedDate }}
                  />
                  <span
                    className="publication-info inline-block"
                    dangerouslySetInnerHTML={{ __html: lastVisibleDate }}
                  />
                </div>
              )}
              <Button
                variant="outlined"
                onClick={() => {
                  handlePrintAction({
                    planUnits: selectedPlanUnits
                      .map(({ name }) => name)
                      .join(', '),
                    interval: getIntervalTitle(interval),
                    lastPublishedDate: publicationInfo
                      ? lastPublishedDate
                      : 'N/A',
                    lastVisibleDate: publicationInfo ? lastVisibleDate : 'N/A'
                  });
                }}
                size="small"
                style={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
              >
                {t('printRoster')}
              </Button>
            </>
          )}
        </div>
      </div>
    </HeaderStyles>
  );
}
